import React, { useMemo, useState } from 'react';

const ConfigPanel = ({ config, updateConfig, isProcessing }) => {
  const [showLargeInputTips, setShowLargeInputTips] = useState(false);

  // ✅ NEW: Logic to handle multi-select
  const handleQuizTypeChange = (typeId) => {
    const currentTypes = Array.isArray(config.quizType) ? config.quizType : [];
    
    if (currentTypes.includes(typeId)) {
      // Don't allow unchecking if it's the last one selected
      if (currentTypes.length > 1) {
        updateConfig('quizType', currentTypes.filter(t => t !== typeId));
      }
    } else {
      updateConfig('quizType', [...currentTypes, typeId]);
    }
  };

  const quizOptions = [
    { id: 'mcq', label: 'Multiple Choice' },
    { id: 'true-false', label: 'True / False' },
    { id: 'identification', label: 'Identification' },
    { id: 'fill-blanks', label: 'Fill in the Blanks' }
  ];

  const tokenPressureRisk = useMemo(() => {
    const qCount = Number(config.questionCount || 0);
    const fCount = Number(config.flashcardCount || 0);
    const summaryOn = config.summaryEnabled !== false;
    const quizOn = config.quizEnabled !== false;
    const flashOn = config.flashcardsEnabled !== false;

    return (
      summaryOn &&
      (
        (quizOn && qCount >= 30) ||
        (flashOn && fCount >= 30) ||
        (quizOn && flashOn && (qCount >= 20 || fCount >= 20))
      )
    );
  }, [config.summaryEnabled, config.quizEnabled, config.flashcardsEnabled, config.questionCount, config.flashcardCount]);

  return (
    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm h-fit">
      <h3 className="font-bold text-gray-900 text-lg mb-4">AI Configuration</h3>

      <div className={`mb-4 rounded-xl border p-3 ${tokenPressureRisk ? 'border-amber-300 bg-amber-50' : 'border-blue-200 bg-blue-50/40'}`}>
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs leading-relaxed text-gray-700">
            <span className="font-bold text-gray-900">Large material tip:</span> If your content is very long, keep quiz and flashcards moderate.
            Very high quiz/flashcard targets can consume too much token budget, which may reduce summary quality.
          </p>
          {tokenPressureRisk && (
            <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-1 rounded">
              High Risk
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowLargeInputTips((prev) => !prev)}
          className="mt-2 text-xs font-semibold text-blue-700 hover:text-blue-800 transition-colors"
        >
          {showLargeInputTips ? 'Hide optimization tips' : 'Show optimization tips'}
        </button>

        {showLargeInputTips && (
          <div className="mt-2 text-[11px] text-gray-600 leading-relaxed space-y-1">
            <p>- For long uploads, start around 10-20 quiz items.</p>
            <p>- Avoid maxing flashcards. Try 8-20 first, then increase later if needed.</p>
            <p>- If the summary looks weak or incomplete, reduce quiz + flashcard counts before retrying.</p>
          </div>
        )}
      </div>
      
      <div className="space-y-4 mb-8">
        {/* 1. Summary Toggle */}
        <div className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-colors ${config.summaryEnabled ? 'bg-blue-50/50 border-blue-200' : 'border-gray-100'}`} onClick={() => updateConfig('summaryEnabled', !config.summaryEnabled)}>
          <span className="text-sm font-bold text-gray-800">Smart Summary</span>
          <input type="checkbox" checked={config.summaryEnabled} readOnly className="accent-blue-600 w-5 h-5 rounded cursor-pointer" />
        </div>

        {/* 2. Flashcards Toggle & Config */}
        <div className={`border rounded-xl p-3 transition-colors ${config.flashcardsEnabled ? 'border-blue-200 bg-blue-50/20' : 'border-gray-100'}`}>
          <div className="flex items-center justify-between cursor-pointer" onClick={() => updateConfig('flashcardsEnabled', !config.flashcardsEnabled)}>
            <span className="text-sm font-bold text-gray-800">Flashcards</span>
            <input type="checkbox" checked={config.flashcardsEnabled} readOnly className="accent-blue-600 w-5 h-5 rounded cursor-pointer" />
          </div>
          
          {config.flashcardsEnabled && (
            <div className="mt-3 animate-[fadeIn_0.2s]">
               <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Count</label>
               <input 
                 type="number" 
                 min="1" max="50" 
                 value={config.flashcardCount} 
                 onChange={(e) => updateConfig('flashcardCount', e.target.value)} 
                 className="w-full p-2 text-xs border rounded-lg mt-1 outline-none focus:border-blue-400" 
                 placeholder="10" 
               />
            </div>
          )}
        </div>

        {/* 3. Quiz Toggle & Config */}
        <div className={`border rounded-xl p-3 transition-colors ${config.quizEnabled ? 'border-blue-200 bg-blue-50/20' : 'border-gray-100'}`}>
          <div className="flex items-center justify-between mb-1 cursor-pointer" onClick={() => updateConfig('quizEnabled', !config.quizEnabled)}>
            <span className="text-sm font-bold text-gray-800">Practice Quiz</span>
            <input type="checkbox" checked={config.quizEnabled} readOnly className="accent-blue-600 w-5 h-5 rounded cursor-pointer" />
          </div>

          {config.quizEnabled && (
            <div className="space-y-3 mt-3 animate-[fadeIn_0.2s]">
              
              {/* ✅ UPDATED: Multiple Selection for Quiz Type */}
              <div>
                 <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide block mb-2">Question Types</label>
                 <div className="grid grid-cols-1 gap-2 bg-white p-2 rounded-lg border border-gray-100">
                    {quizOptions.map((type) => (
                      <label key={type.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1.5 rounded transition-colors select-none">
                        <input 
                          type="checkbox" 
                          checked={config.quizType?.includes(type.id)}
                          onChange={() => handleQuizTypeChange(type.id)}
                          className="accent-blue-600 w-4 h-4 rounded cursor-pointer"
                        />
                        <span className="text-xs text-gray-700 font-medium">{type.label}</span>
                      </label>
                    ))}
                 </div>
                 <p className="text-[10px] text-gray-400 mt-1 pl-1">Select at least one type.</p>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                 {/* Question Count */}
                 <div>
                     <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Items</label>
                     <input 
                       type="number" min="1" max="50" 
                       value={config.questionCount} 
                       onChange={(e) => updateConfig('questionCount', e.target.value)} 
                       className="w-full p-2 text-xs border rounded-lg mt-1 outline-none focus:border-blue-400" 
                     />
                 </div>
                 {/* Time per Question */}
                 <div>
                     <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Secs/Q</label>
                     <input 
                       type="number" min="5" max="300" step="5" 
                       value={config.timePerQuestion} 
                       onChange={(e) => updateConfig('timePerQuestion', e.target.value)} 
                       className="w-full p-2 text-xs border rounded-lg mt-1 outline-none focus:border-blue-400" 
                       placeholder="30" 
                     />
                 </div>
              </div>
            </div>
          )}
        </div>

        {/* 4. Quiz Difficulty (Visible only when Practice Quiz is enabled) */}
        {config.quizEnabled && (
          <div className="pt-2 animate-[fadeIn_0.2s]">
            <div className="flex justify-between items-center mb-1">
               <label className="text-[10px] font-bold text-gray-500 uppercase">Difficulty</label>
               <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                  {config.difficulty === 1 ? 'Easy' : config.difficulty === 2 ? 'Normal' : 'Hard'}
               </span>
            </div>
            <input 
              type="range" min="1" max="3" 
              value={config.difficulty} 
              onChange={(e) => updateConfig('difficulty', Number(e.target.value))} 
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600" 
            />
          </div>
        )}
      </div>

      {/* 5. Custom Instructions */}
      <div className="mb-4">
        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Custom Instructions</label>
        <textarea 
          value={config.customInstructions} 
          onChange={(e) => updateConfig('customInstructions', e.target.value)} 
          className="w-full p-3 rounded-xl border border-gray-200 outline-none text-xs bg-gray-50 h-20 resize-none focus:bg-white focus:border-blue-300 transition-all" 
          placeholder="e.g. Focus on dates, specific terminologies, or strict definitions..."
        ></textarea>
      </div>
    </div>
  );
};

export default ConfigPanel;
